import axios from 'axios';
import { config } from '../config';

// One session ID per browser tab — ties all requests in this visit together in Seq.
function getOrCreateSessionId(): string {
  let id = sessionStorage.getItem('sessionId');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('sessionId', id);
  }
  return id;
}

const _sessionId = getOrCreateSessionId();

const apiClient = axios.create({
  baseURL: config.apiUrl || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Derive tenant from the first URL path segment: /{tenantName}/...
const tenantName = window.location.pathname.split('/').filter(Boolean)[0] ?? '';

// Module-level API key — set via setClientApiKey(). Uses localStorage (not
// sessionStorage) so the member stays logged in across app closes/reopens on
// mobile -- the JWT itself is already valid for 30 days, but sessionStorage was
// wiped whenever iOS/Android tore down the web view between launches, e.g. for a
// home-screen PWA relaunched from a fully-closed state.
let _apiKey: string | null = localStorage.getItem('apiKey');

export function setClientApiKey(key: string | null): void {
  _apiKey = key;
  if (key) {
    localStorage.setItem('apiKey', key);
  } else {
    localStorage.removeItem('apiKey');
  }
}

// Attach API key, tenant, correlation and session headers on every request.
// A fresh CorrelationId per request lets Seq group all server-side logs for
// that one call; SessionId groups every call made during this browser session.
apiClient.interceptors.request.use((req) => {
  if (_apiKey) {
    req.headers['Authorization'] = `Bearer ${_apiKey}`;
  }
  if (tenantName) {
    req.headers['X-Tenant-ID'] = tenantName;
  }
  req.headers['X-Correlation-Id'] = crypto.randomUUID();
  req.headers['X-Session-Id'] = _sessionId;
  return req;
});

// Handle global response errors
apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const message: string = error.response?.data?.message ?? '';

    // Unknown tenant — show a hard stop, not a login redirect
    if (error.response?.status === 400 && message.startsWith('Unknown tenant:')) {
      document.body.innerHTML = `
        <div style="font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;background:#0f0f23;color:#fff;">
          <div style="text-align:center;padding:32px;">
            <h2 style="margin-bottom:12px;">Invalid Gym</h2>
            <p style="color:rgba(255,255,255,0.6)">The gym you're trying to access does not exist or is no longer active.</p>
          </div>
        </div>`;
      return Promise.reject(error);
    }

    // Redirect to login on 401
    if (error.response?.status === 401) {
      setClientApiKey(null);
      localStorage.removeItem('member');
      window.location.href = 'login';
    }

    return Promise.reject(error);
  }
);

// Post a client-side error to the API so it appears in Seq alongside server logs.
// The request interceptor attaches X-Correlation-Id and X-Session-Id automatically,
// linking the entry to the current session in Seq.
// Silently swallowed — error reporting must never crash the app.
export async function postClientError(payload: {
  level?: string;
  message: string;
  stack?: string;
  url?: string;
  component?: string;
}): Promise<void> {
  try {
    await apiClient.post('/clientlog', { app: 'FitTrack-PWA', ...payload });
  } catch {
    // intentionally silent
  }
}

export default apiClient;
